import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import type { RootState } from "../../store";
import {
  fetchAgentsAdmin,
  createAgent,
  updateAgent,
  setAgentStatus,
  deleteAgent,
  resetSaveState,
  resetStatusError,
  resetDeleteError,
} from "../../store/agentManagementSlice";
import type { ManagedAgent, AgentFormPayload } from "../../store/agentManagementSlice";
import { fetchAgentLookups } from "../../store/agentManagementSlice";
import { useMsal } from "@azure/msal-react";
import { clearUser } from "../../store/authSlice";
import Sidebar from "../../components/Sidebar";
import {
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControlLabel,
  Switch,
  Grid,
  MenuItem,
  Link,
  Snackbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import "../Dashboard.scss";
import styles from "./agentmanagement.module.scss";
import { useNavigate } from "react-router-dom";

const EMPTY_FORM: AgentFormPayload = {
  agent_name: "",
  short_description: "",
  category_id: null,
  endpoint: "",
  tags: [],
  agent_photo: "",
  agent_icon: "",
  agent_intro_video_path: "",
  agent_readme_path: "",
  presentation_link: "",
  brochure_link: "",
  resources_link: "",
  how_to_use: "",
  instructions_set: "",
  default_prompts: "",
  efficiency_note: "",
  status: 1,
  is_display: true,
  is_featured: false,
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export default function AgentManagementPage(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    agents,
    loading,
    error,
    saveLoading,
    saveSuccess,
    saveError,
    statusUpdatingAgentId,
    statusError,
    deletingAgentId,
    deleteError,
  } = useSelector((state: RootState) => state.agentManagement);

  const { items: lookupItems } = useSelector(
    (state: RootState) => state.agentLookup
  );
  const categoryOptions = useMemo(
    () => lookupItems.map((item) => ({ id: item.category_id, label: item.category })),
    [lookupItems]
  );

  const [searchInputValue, setSearchInputValue] = useState("");
  const debouncedSearchTerm = useDebounce(searchInputValue, 300);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formValues, setFormValues] = useState<AgentFormPayload>(EMPTY_FORM);
  const [editingAgentId, setEditingAgentId] = useState<number | null>(null);
  const [tagsInput, setTagsInput] = useState("");
  const [formTouched, setFormTouched] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAgentsAdmin({ limit: 500 }));
    dispatch(fetchAgentLookups());
  }, [dispatch]);

  useEffect(() => {
    if (!saveSuccess) return;
    const timer = setTimeout(() => dispatch(resetSaveState()), 3000);
    return () => {
      clearTimeout(timer);
      dispatch(resetSaveState());
    };
  }, [saveSuccess, dispatch]);

  const onClickLogout = () => {
    dispatch(clearUser());
    instance.logoutRedirect({ postLogoutRedirectUri: "/" });
  };

  const filteredAgents = useMemo(() => {
    const term = debouncedSearchTerm.toLowerCase().trim();
    if (!term) return agents;
    return agents.filter((agent: ManagedAgent) => {
      return (
        agent.agent_name?.toLowerCase().includes(term) ||
        agent.category?.toLowerCase().includes(term) ||
        agent.agent_type?.toLowerCase().includes(term)
      );
    });
  }, [agents, debouncedSearchTerm]);

  const handleClearSearch = () => setSearchInputValue("");

  const openCreateForm = () => {
    setFormMode("create");
    setEditingAgentId(null);
    setFormValues(EMPTY_FORM);
    setTagsInput("");
    setFormTouched(false);
    setFormOpen(true);
  };

  const openEditForm = (agent: ManagedAgent) => {
    setFormMode("edit");
    setEditingAgentId(agent.id);
    setFormValues({
      agent_name: agent.agent_name || "",
      short_description: agent.short_description || "",
      category_id: agent.category_id ?? null,
      endpoint: agent.endpoint || "",
      tags: agent.tags || [],
      agent_photo: agent.agent_photo || "",
      agent_icon: agent.agent_icon || "",
      agent_intro_video_path: agent.agent_intro_video_path || "",
      agent_readme_path: agent.agent_readme_path || "",
      presentation_link: agent.presentation_link || "",
      brochure_link: agent.brochure_link || "",
      resources_link: agent.resources_link || "",
      how_to_use: agent.how_to_use || "",
      instructions_set: agent.instructions_set || "",
      default_prompts: agent.default_prompts || "",
      efficiency_note: agent.efficiency_note || "",
      status: agent.status ?? 1,
      is_display: agent.is_display ?? true,
      is_featured: !!agent.is_featured,
    });
    setTagsInput((agent.tags || []).join(", "));
    setFormTouched(false);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saveLoading) return;
    setFormOpen(false);
    setDeleteConfirmOpen(false);
  };

  const handleFormChange = (field: keyof AgentFormPayload, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormTouched(true);

    if (!formValues.agent_name || !formValues.agent_name.trim()) {
      return;
    }

    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const payload: AgentFormPayload = { ...formValues, tags };

    try {
      if (formMode === "create") {
        await dispatch(createAgent(payload)).unwrap();
      } else if (editingAgentId != null) {
        await dispatch(updateAgent({ agentId: editingAgentId, data: payload })).unwrap();
      }
      setFormOpen(false);
    } catch {
      // error surfaced via saveError alert
    }
  };

  const handleToggleStatus = (agent: ManagedAgent) => {
    const nextStatus = agent.status === 1 ? 0 : 1;
    dispatch(setAgentStatus({ agentId: agent.id, status: nextStatus }));
  };

  const handleDeleteAgent = async () => {
    if (editingAgentId == null) return;
    const agentName = formValues.agent_name || "Agent";
    try {
      await dispatch(deleteAgent(editingAgentId)).unwrap();
      setDeleteConfirmOpen(false);
      setFormOpen(false);
      setDeleteSuccessMessage(`"${agentName}" deleted successfully.`);
    } catch {
      // error surfaced via deleteError alert
    }
  };

  return (
    <div className="dashboard-root">
      <Sidebar
        activeNav="agent-management"
        user={user || undefined}
        onLogout={onClickLogout}
      />
      <main className="main-area">
        <header className="header">
          <div className="header-left">
            <IconButton
              onClick={() => navigate("/landing")}
              sx={{ mr: 2 }}
              aria-label="Back to landing page"
            >
              <ArrowBackIcon />
            </IconButton>
            <h1 className="page-title">Agent Management</h1>
          </div>
        </header>

        <div className="scrollContent">
          <div className={styles.content}>
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            )}

            {!loading && error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && (
              <>
                {saveSuccess && (
                  <Alert severity="success" sx={{ mb: 3 }} onClose={() => dispatch(resetSaveState())}>
                    Agent saved successfully!
                  </Alert>
                )}
                {saveError && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(resetSaveState())}>
                    {saveError}
                  </Alert>
                )}
                {statusError && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(resetStatusError())}>
                    {statusError}
                  </Alert>
                )}
                {deleteError && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(resetDeleteError())}>
                    {deleteError}
                  </Alert>
                )}
                <Card className={styles.agentsCard} elevation={2}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <Typography variant="h6">
                        All Agents ({filteredAgents.length})
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <TextField
                          size="small"
                          placeholder="Search by name, category or type"
                          value={searchInputValue}
                          onChange={(e) => setSearchInputValue(e.target.value)}
                          sx={{ minWidth: 280 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: searchInputValue && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={handleClearSearch} edge="end">
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={openCreateForm}
                        >
                          Add Agent
                        </Button>
                      </Box>
                    </Box>

                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 600, overflowY: "auto" }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Agent</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredAgents.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                  {debouncedSearchTerm
                                    ? "No agents found for your search"
                                    : "No agents found. Click \"Add Agent\" to create one."}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredAgents.map((agent) => (
                              <TableRow key={agent.id}>
                                <TableCell>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <SmartToyOutlinedIcon fontSize="small" color="action" />
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>
                                        {agent.agent_name}
                                      </Typography>
                                      {agent.short_description && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                          }}
                                        >
                                          {agent.short_description}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {agent.category || "—"}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Edit agent">
                                    <IconButton size="small" onClick={() => openEditForm(agent)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Create / Edit Agent Dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmitForm}>
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {formMode === "create" ? "Add Agent" : "Edit Agent"}
            <IconButton onClick={closeForm} disabled={saveLoading} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Agent Name"
                  required
                  fullWidth
                  value={formValues.agent_name}
                  onChange={(e) => handleFormChange("agent_name", e.target.value)}
                  error={formTouched && !formValues.agent_name?.trim()}
                  helperText={
                    formTouched && !formValues.agent_name?.trim()
                      ? "Agent name is required"
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Short Description"
                  fullWidth
                  multiline
                  minRows={2}
                  value={formValues.short_description}
                  onChange={(e) => handleFormChange("short_description", e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Category"
                  fullWidth
                  value={formValues.category_id ?? ""}
                  onChange={(e) =>
                    handleFormChange(
                      "category_id",
                      e.target.value === "" ? null : Number(e.target.value)
                    )
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {categoryOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate("/agent-lookup-management")}
                >
                  Manage categories
                </Link>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Endpoint"
                  fullWidth
                  value={formValues.endpoint}
                  onChange={(e) => handleFormChange("endpoint", e.target.value)}
                  placeholder="/stream/example_agent"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Tags (comma separated)"
                  fullWidth
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="e.g. contracts, legal, review"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Agent Photo URL"
                  fullWidth
                  value={formValues.agent_photo}
                  onChange={(e) => handleFormChange("agent_photo", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Agent Icon URL"
                  fullWidth
                  value={formValues.agent_icon}
                  onChange={(e) => handleFormChange("agent_icon", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Agent Video URL"
                  fullWidth
                  value={formValues.agent_intro_video_path}
                  onChange={(e) => handleFormChange("agent_intro_video_path", e.target.value)}
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Presentation Link"
                  fullWidth
                  value={formValues.presentation_link}
                  onChange={(e) => handleFormChange("presentation_link", e.target.value)}
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Brochure Link"
                  fullWidth
                  value={formValues.brochure_link}
                  onChange={(e) => handleFormChange("brochure_link", e.target.value)}
                  placeholder="https://..."
                  helperText="Paste the full link to the brochure - a bare filename won't open."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Resources Link (SharePoint)"
                  fullWidth
                  value={formValues.resources_link}
                  onChange={(e) => handleFormChange("resources_link", e.target.value)}
                  placeholder="Paste the SharePoint folder link"
                  helperText="All resources for this agent (decks, one-pagers, etc.) should be placed in a SharePoint folder — paste the folder link here."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="How To Use"
                  fullWidth
                  multiline
                  minRows={2}
                  value={formValues.how_to_use}
                  onChange={(e) => handleFormChange("how_to_use", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Instructions Set"
                  fullWidth
                  multiline
                  minRows={3}
                  value={formValues.instructions_set}
                  onChange={(e) => handleFormChange("instructions_set", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Default Prompts"
                  fullWidth
                  multiline
                  minRows={2}
                  value={formValues.default_prompts}
                  onChange={(e) => handleFormChange("default_prompts", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Efficiency Note"
                  fullWidth
                  multiline
                  minRows={2}
                  value={formValues.efficiency_note}
                  onChange={(e) => handleFormChange("efficiency_note", e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.status === 1}
                      onChange={(e) => handleFormChange("status", e.target.checked ? 1 : 0)}
                    />
                  }
                  label="Active / Visible"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formValues.is_display}
                      onChange={(e) => handleFormChange("is_display", e.target.checked)}
                    />
                  }
                  label="Display in listing"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formValues.is_featured}
                      onChange={(e) => handleFormChange("is_featured", e.target.checked)}
                    />
                  }
                  label="Featured"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {formMode === "edit" && (
              <Button
                onClick={() => setDeleteConfirmOpen(true)}
                color="error"
                startIcon={<DeleteIcon fontSize="small" />}
                disabled={saveLoading || deletingAgentId === editingAgentId}
                sx={{ mr: "auto" }}
              >
                Delete
              </Button>
            )}
            <Button onClick={closeForm} color="inherit" disabled={saveLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saveLoading}
              startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {saveLoading ? "Saving..." : formMode === "create" ? "Create Agent" : "Save Changes"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Agent?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{formValues.agent_name || "this agent"}</strong>? It will
            no longer appear in the agent list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            color="inherit"
            disabled={deletingAgentId === editingAgentId}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAgent}
            color="error"
            variant="contained"
            disabled={deletingAgentId === editingAgentId}
            startIcon={
              deletingAgentId === editingAgentId ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {deletingAgentId === editingAgentId ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!deleteSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setDeleteSuccessMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setDeleteSuccessMessage(null)}
        >
          {deleteSuccessMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
